import React from 'react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, Legend, ReferenceLine
} from 'recharts';
import { Heatmap } from './DashboardShared';
import { KpiGrid, KpiCard, Panel } from '../../components/design';

export default function DashboardAI({
  aiStats, chatopsStats, mttrTrend, aiEffectiveness, severityTimeline, incidentTimeline,
}) {
  const mttrData = mttrTrend.map(m => ({ ...m, avg_minutes: Math.round((m.avg_seconds || 0) / 60) }));

  return (
    <div>
      <KpiGrid>
        {aiStats.total_analyses_24h !== undefined && (
          <KpiCard label="Analyses (24h)" value={aiStats.total_analyses_24h} countUp icon="brain" iconBg="rgba(96,165,250,0.10)" iconColor="#60a5fa" />
        )}
        {aiStats.avg_duration_sec !== undefined && (
          <KpiCard label="Avg Duration" value={`${aiStats.avg_duration_sec?.toFixed(1)}s`} icon="timer" iconBg="rgba(167,139,250,0.10)" iconColor="#a78bfa" />
        )}
        <KpiCard label="ChatOps Alerts" value={chatopsStats.alerts_sent_24h || 0} countUp icon="bell" iconBg="rgba(52,211,153,0.10)" iconColor="#34d399" />
        <KpiCard label="Chat Commands" value={chatopsStats.commands_received || 0} countUp icon="message-square" iconBg="rgba(251,191,36,0.10)" iconColor="#fbbf24" />
      </KpiGrid>

      <div className="grid grid-2 section-gap">
        <Panel title="MTTR Trend (30d)">
          {mttrData.length > 0 ? (
            <div className="chart-box sm">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={mttrData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--grid-line)" />
                  <XAxis dataKey="date" tick={{ fontSize: 8 }} stroke="var(--text-3)" tickFormatter={v => v?.slice(5)} />
                  <YAxis yAxisId="left" tick={{ fontSize: 8 }} stroke="#8b5cf6" label={{ value: 'min', angle: -90, position: 'insideLeft', fontSize: 9 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 8 }} stroke="#10b981" label={{ value: 'count', angle: 90, position: 'insideRight', fontSize: 9 }} />
                  <Tooltip />
                  <Bar yAxisId="right" dataKey="count" fill="#10b981" fillOpacity={0.5} radius={[3, 3, 0, 0]} />
                  <Line yAxisId="left" type="monotone" dataKey="avg_minutes" stroke="#8b5cf6" strokeWidth={2} dot={false} />
                  <Legend iconSize={8} wrapperStyle={{ fontSize: 9, color: 'var(--text-2)' }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          ) : <div className="empty">No MTTR data</div>}
        </Panel>
        <Panel title="AI Effectiveness (30d)">
          {aiEffectiveness.length > 0 ? (
            <div className="chart-box sm">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={aiEffectiveness}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--grid-line)" />
                  <XAxis dataKey="date" tick={{ fontSize: 8 }} stroke="var(--text-3)" tickFormatter={v => v?.slice(5)} />
                  <YAxis yAxisId="left" tick={{ fontSize: 8 }} stroke="#ec4899" domain={[0, 100]} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 8 }} stroke="#6366f1" />
                  <Tooltip />
                  <Bar yAxisId="right" dataKey="analyses" fill="#6366f1" fillOpacity={0.4} radius={[3, 3, 0, 0]} />
                  <Line yAxisId="left" type="monotone" dataKey="avg_confidence" stroke="#ec4899" strokeWidth={2} dot={false} />
                  <ReferenceLine yAxisId="left" y={80} stroke="#f59e0b" strokeDasharray="3 3" />
                  <Legend iconSize={8} wrapperStyle={{ fontSize: 9, color: 'var(--text-2)' }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          ) : <div className="empty">No AI data</div>}
        </Panel>
      </div>

      <div className="grid grid-2 section-gap">
        <Panel title="Incident Heatmap (30d)">
          <Heatmap data={severityTimeline} />
          <div className="flex items-center gap-1 mt-1.5 muted"><span className="w-3 h-3 rounded-sm" style={{ background: 'var(--bg-3)' }} />0 <span className="w-3 h-3 rounded-sm bg-green-200" />Low <span className="w-3 h-3 rounded-sm bg-amber-400" />Mid <span className="w-3 h-3 rounded-sm bg-red-400" />High</div>
        </Panel>
        <Panel title="Incident Timeline (24h)">
          {incidentTimeline.length > 0 ? (
            <div className="chart-box sm">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={incidentTimeline}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--grid-line)" />
                  <XAxis dataKey="hour" tick={{ fontSize: 9 }} stroke="var(--text-3)" />
                  <YAxis tick={{ fontSize: 9 }} stroke="var(--text-3)" />
                  <Tooltip />
                  <Area type="monotone" dataKey="count" stroke="#6366f1" fill="#6366f1" fillOpacity={0.2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : <div className="empty">No incidents</div>}
        </Panel>
      </div>
    </div>
  );
}
